<template>
  <div class="page">
    <!-- OVERLAY FOR RESULTS UPDATE -->
    <v-overlay
      absolute
      :value="isUpdateQuoteShow && $vuetify.breakpoint.lgAndUp"
    />

    <!-- HEADER -->
    <site-header ref="header" />

    <!-- SMALL DEVICES ADJUST OPTIONS -->
    <v-sheet
      v-if="$vuetify.breakpoint.mdAndDown"
      color="navy-medium"
      dark
      tile
      class="px-3 d-flex align-center"
    >
      <v-col>
        <v-btn
          outlined
          rounded
          small
          color="white"
          data-test="adjust_your_cover"
          @click="isOptionsDrawerVisible = !isOptionsDrawerVisible"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-tune
          </v-icon>
          Adjust your cover
        </v-btn>
      </v-col>
      <v-col>
        <v-switch
          v-model="isViewMonthly"
          data-test="view_monthly"
          :label="isViewMonthly ? 'Monthly cost' : 'Annual cost'"
          inset
          hide-details
          class="mt-0 pt-0 payment-type-switch"
        />
      </v-col>
    </v-sheet>

    <!-- RESULTS TABLE / RESULTS FILTERS -->
    <v-container
      v-if="hasSucceededQuotes || !isQuotesLoading"
      :fluid="$vuetify.breakpoint.lgAndDown"
      class="py-4 px-md-8"
    >
      <v-row class="my-0">
        <!-- RESULTS FILTERS -->
        <v-col
          cols="12"
          lg="3"
          class="py-0"
        >
          <v-navigation-drawer
            v-model="isOptionsDrawerVisible"
            :app="$vuetify.breakpoint.mdAndDown"
            :right="$vuetify.breakpoint.mdAndDown"
            align-self="end"
            clipped
            floating
            touchless
            :color="$vuetify.breakpoint.lgAndUp ? 'transparent' : undefined"
            :width="$vuetify.breakpoint.lgAndUp ? 400 : 360"
            :height="$vuetify.breakpoint.lgAndUp ? 'auto' : '100%'"
            :permanent="$vuetify.breakpoint.lgAndUp"
            :style="$vuetify.breakpoint.lgAndUp && `position:sticky; top: ${fixedTopIndent}px;`"
            :class="[ $vuetify.breakpoint.lgAndUp && 'fix-height',
                      isQuotesLoading && 'v-navigation-drawer-disabled',
                      isUpdateQuoteShow && 'requote-shown' ]"
          >
            <results-options
              v-if="formData"
              :form-data="formData"
              :options="filterOptions"
              :selected-filters="selectedFilters"
              :cover-type-related-label="coverTypeRelatedLabel"
              @click-edit-quote="goToForm"
              @click-close="isOptionsDrawerVisible = !isOptionsDrawerVisible"
              @click-details="openDetailsDrawer"
              @change-filter="handleChangeFilter"
            />
          </v-navigation-drawer>
        </v-col>

        <!-- RESULTS TABLE -->
        <v-col
          cols="12"
          lg="9"
          class="py-0 d-flex flex-column flex-grow-1"
        >
          <main
            class="flex-grow-1"
          >
            <results-table
              :quote-ref="quoteRef"
              :has-pre-line-text="false"
              :quotes-qty="quotesQuantity"
              :columns="resultsTableColumns"
              :insured-name="insuredName"
              :all-results-qty="allResults.length"
              :failed-quotes="failedResults"
              :is-no-results="isNoResults"
              :has-quote-summary="!!quoteSummary"
              :selected-optional-extras="[]"
              :fixed-top-indent="fixedTopIndent"
              :custom-info-message="customInfoMessage"
              :has-broker-results="hasBrokerResults"
              :is-broker-solution-message-shown="isBrokerSolutionMessageShown"
              :is-broker-solution-table-shown="isBrokerSolutionTableShown"
              :broker-quotes-qty="brokerShownResults.length"
              @open-drawer-details="openDetailsDrawer"
              @go-to-form="goToForm"
            >
              <!-- BROKER NOTIFICATIONS -->
              <template
                v-if="isBrokerSolutionMessageShown"
                #brokerResultsMessage
              >
                <span
                  v-html="brokerResultsMessage"
                />
              </template>
              <!-- BROKER CUSTOM MESSAGE -->
              <template
                v-if="config.partnerMessage"
                #partnerMessage
              >
                <div v-html="config.partnerMessage" />
              </template>
              <!-- BROKER RESULTS -->
              <template
                v-if="isBrokerSolutionTableShown"
                #brokerResults
              >
                <div
                  v-for="(chosenQuote, index) of brokerShownResults"
                  :id="'chosen-quote-'+chosenQuote.id"
                  :key="'chosen-quote-'+chosenQuote.id"
                  class="quote-container mb-6"
                >
                  <results-quote
                    :quote="chosenQuote"
                    :is-view-monthly="isViewMonthly"
                    :cover-type-related-label="handleVetsFeeLabelVariations(chosenQuote.details[0].coverType)"
                    :details-description="getDetailsDescription"
                    @click-view-details="clickViewDetails($event, index)"
                    @open-drawer-details="openDetailsDrawer"
                    @toggle-quote-shown="toggleQuoteShown"
                  />
                </div>
              </template>
              <!-- QZ NOTIFICATION -->
              <template
                v-if="isQZShownMessageShown"
                #QZMessage
              >
                <results-broker-notification
                  v-if="isBrokerSolutionEnabled"
                  :has-broker-results="hasBrokerResults"
                  :quotes-qty="shownResults.length"
                  :shown-results-cheapest-price="shownResultsCheapestPrice"
                />
                <span
                  v-else
                  v-html="QZMessage"
                />
              </template>

              <!-- ALL RESULTS -->
              <template
                v-if="isQZShown"
                #quotes
              >
                <div
                  v-for="(quote, index) of shownResults"
                  :key="quote.id"
                  class="quote-container mb-6"
                >
                  <transition name="fade-in">
                    <results-quote
                      v-show="quote.isVisible"
                      :quote="quote"
                      :is-view-monthly="isViewMonthly"
                      :cover-type-related-label="handleVetsFeeLabelVariations(quote.details[0].coverType)"
                      :details-description="getDetailsDescription"
                      @click-view-details="clickViewDetails($event, index)"
                      @open-drawer-details="openDetailsDrawer"
                      @toggle-quote-shown="toggleQuoteShown"
                    />
                  </transition>
                </div>
              </template>
            </results-table>
            <results-additional-quotes
              v-if="isAdditionalResultsCTAShown"
              @get-more-quotes="onBrokerSolutionModalSuccess"
            />
          </main>
        </v-col>
      </v-row>

      <!-- FOOTER -->
      <v-footer
        v-if="config.partnerFooter"
        padless
        color="transparent"
        tag="div"
      >
        <site-footer role="footer">
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>
      </v-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <!-- RESULTS FOUND SMALL MODAL -->
      <loading-notice
        :is-only-loading-shown="isBrokerSolutionEnabled"
        :is-loading="isQuotesLoading"
        :totalcount="quotesQuantity"
      />
    </v-container>

    <!-- ASIDE DRAWER -->
    <v-navigation-drawer
      v-model="isDetailsDrawerOpen"
      app
      right
      temporary
      touchless
      :width="drawerWidth"
      height="100%"
    >
      <keep-alive>
        <component
          :is="drawerComponent"
          ref="drawer"
          :quote="detailsQuote"
          :details-description="getDetailsDescriptionFilteredByCoverType"
          :quote-position-number="quotePositionNumber"
          :is-view-monthly="isViewMonthly"
          :is-details-drawer-open="isDetailsDrawerOpen"
          :selected-optional-extras="optionalExtras"
          :quote-summary="quoteSummary"
          :form-step-labels="formStepLabels"
          :product-name="productName"
          :cover-type-details="getInitDetailsDescription.coverTypes.content"
          :cover-type-related-label="coverTypeRelatedLabel"
          @open-drawer-details="openDetailsDrawer"
          @go-to-form="goToForm"
          @close-drawer="closeDetailsDrawer"
        />
      </keep-alive>
    </v-navigation-drawer>
    <!-- BROKER SOLUTION EXIT ONLY MODAL -->
    <results-modal-alerts
      v-if="isBrokerSolutionModalShowed"
      ref="brokerModal"
      modal-alert="brokerModal"
      @success="onBrokerSolutionModalSuccess"
      @cancel="onBrokerSolutionModalCancel"
    />

    <!-- RESULTS FAILED MODAL -->
    <results-modal-alerts
      :modal-alert="modalAlert"
      @go-to-form="goToForm"
      @cancel="modalAlert = false"
    />

    <!-- RESULTS LOADING MODAL -->
    <results-loading-modal
      :quoting-done-percentage="quotingDonePercentage"
      :has-succeeded-quotes="hasSucceededQuotes"
      :is-quotes-loading="isQuotesLoading"
    />
  </div>
</template>

<script>
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneResult from '@/composables/useQuotezoneResult';
import useBrokerSolution from '@/composables/useBrokerSolution';

import ResultsTable from '@/components/car/Results/ResultsTable.vue';
import DetailsQuoteSummary from '@/components/car/Results/detailsDrawer/DetailsQuoteSummary.vue';
import DetailsDescription from '@/components/car/Results/detailsDrawer/DetailsDescription.vue';
import ResultsModalAlerts from '@/components/car/Results/ResultsModalAlerts.vue';
import LoadingNotice from '@/components/car/Results/ResultsLoadingNotice.vue';
import ResultsDetails from '@/components/pet/Results/ResultsDetails.vue';
import ResultsQuote from '@/components/pet/Results/ResultsQuote/ResultsQuote.vue';
import ResultsOptions from '@/components/pet/Results/ResultsOptions.vue';
import ResultsLoadingModal from '@/components/ResultsLoadingModal';
import ModalAlert from '@/components/car/Results/ModalAlert';
import ResultsBrokerNotification from '@/components/car/Results/ResultsBrokerNotification';
import ResultsAdditionalQuotes from '@/components/car/Results/ResultsAdditionalQuotes.vue';

import { UtcDate } from '@/helpers/date';
import { inject } from '@vue/composition-api';

export default {
  name: 'page-pet-insurance-results',
  inject: [
    'config',
    'constants',
    'formMetadata',
    'helpers',
  ],
  metaInfo () {
    return {
      title: `${this.config.partnerText} | ${this.formMetadata.productTitle} Insurance | Quotes`,
    };
  },
  components: {
    ResultsDetails,
    ResultsOptions,
    ResultsTable,
    DetailsQuoteSummary,
    DetailsDescription,
    ResultsModalAlerts,
    LoadingNotice,
    ResultsQuote,
    ResultsLoadingModal,
    ModalAlert,
    ResultsBrokerNotification,
    ResultsAdditionalQuotes,
  },
  data () {
    return {
      // Pass to DetailsList, based on data get step number
      // when click edit details
      formStepLabels: {
        Pet: '1',
        'Cover Details': '2',
        'About You': '2',
      },
    };
  },
  computed: {
    petAgeLabel () {
      const dateOfInsuranceStart = new UtcDate(this.formData.proposer.dateOfInsuranceStart);
      const dateOfBirth = this.formData.cover.pets[0].petDateOfBirth;
      const petMonths = dateOfInsuranceStart.differenceInMonths(dateOfBirth);
      const petYears = dateOfInsuranceStart.differenceInYears(dateOfBirth);

      if (!petYears) return `${petMonths} month old`;

      return `${petYears} year old`;
    },
    message () {
      const { coverTypes, vetFees } = this.selectedFilters;
      const pets = this.formData.cover.pets;
      const petNameLabel = pets[0].petBreedType !== 'Mixed' ? pets[0].petBreed : pets[0].pet;
      const petLabel = pets.length > 1 ? 'multiple pets' : `a ${this.petAgeLabel} ${petNameLabel}`;
      const vetFeesFormattedText = this.filterOptions.vetFeesOptions.find(option => option.value === this.vetFees)?.text;
      const vetFeesLabel = vetFees ? `cover and <strong>${vetFeesFormattedText}</strong> vet fee limit.` : '';
      const coverTypeLabel = coverTypes ? `with <strong>${coverTypes === 'Accidents only' ? 'Accident Only' : coverTypes}</strong>` : '';

      return `prices for <strong>${petLabel}</strong> ${coverTypeLabel} ${vetFeesLabel}`;
    },
    QZMessage () {
      const name = this.insuredName ? `${this.insuredName}, we're` : "We're";

      return `${name} showing you ${this.message}`;
    },
    brokerResultsMessage () {
      return `${this.config.partnerText} ${this.message}`;
    },
    customInfoMessage () {
      const pets = this.formData.cover.pets;

      return `We have found you <strong>${this.quotesQuantity}</strong> quotes for ${pets.length > 1 ? 'your pets' : pets[0].petName}`;
    },
    coverTypeRelatedLabel () {
      if (!this.restSelectedFilters?.coverTypes) return '';
      return this.handleVetsFeeLabelVariations(this.restSelectedFilters.coverTypes);
    },
    isAccidentsOnlyCoverType () {
      if (!this.restSelectedFilters?.coverTypes) return false;
      return this.restSelectedFilters.coverTypes.toLowerCase() === 'accidents only';
    },
    getDetailsDescriptionFilteredByCoverType () {
      if (this.drawerDetails !== 'vetFees') return this.getDetailsDescription;

      const filteredDetails = { ...this.getDetailsDescription };
      // Keeps all entries that do not have the flag 'showForAccidentsOnlyCover'
      // Also keeps the entries that have the flag 'showForAccidentsOnlyCover' based on the selected cover type and the flag value
      filteredDetails.content = filteredDetails.content.filter(content => content?.showForAccidentsOnlyCover === undefined || content.showForAccidentsOnlyCover === this.isAccidentsOnlyCoverType);
      return filteredDetails;
    },

    resultsTableColumns () {
      return [
        {
          name: 'Provider',
        },
        {
          name: this.isViewMonthly ? 'Monthly' : 'Annually',
        },
        {
          name: 'Cover Type',
          details: 'coverTypes',
        },
        {
          name: `Vet Fee Limits <br/> ${this.coverTypeRelatedLabel}`,
          details: 'vetFees',
        },
        {
          name: 'Vet Fee Limits <br/> Policy Limit',
          details: 'vetFees',
        },
        {
          name: 'Vet Excess',
          details: 'vetFees',
        },
        {
          name: 'Policy Details',
        },
      ];
    },
    filterOptions () {
      return {
        vetFeesOptions: this.constants.VET_FEES_OPTIONS,
        coverTypeOptions: this.constants.COVER_TYPE_OPTIONS,
      };
    },
    selectedFilters () {
      return {
        isViewMonthly: this.isViewMonthly,
        ...this.restSelectedFilters,
      };
    },
  },
  async created () {
    this.initSelectedFilters();
    await this.getQuotes(true);
    if (this.isBrokerSolutionEnabled) {
      this.initExitOnlyModal();
    }
  },
  methods: {
    handleChangeFilter: function ({ filter, value }) {
      this.changeFilter({ filter, value }, this.selectedFilters);
    },
    handleVetsFeeLabelVariations (coverType) {
      return coverType.toLowerCase() === 'accidents only' ? 'Per Accident' : 'Per Condition';
    },
  },
  setup (props, context) {
    const structure = inject('formMetadata');
    const { DETAILS_DESCRIPTION } = inject('constants');
    const stepIds = structure.steps.map(step => step.stepId);
    const common = useQuotezoneCommon(props, context);
    const result = useQuotezoneResult({
      stepIds,
      detailsDescription: DETAILS_DESCRIPTION,
      gtmTracking: common.gtmTracking,
    }, context);
    const broker = useBrokerSolution(result, context);

    return {
      ...common,
      ...result,
      ...broker,
    };
  },
};
</script>

<style lang="scss" src='@/assets/scss/results/core-component.scss'></style>
